import {
  GoogleAuthProvider,
  signInWithPopup,
  type UserCredential,
} from "firebase/auth";
import {
  collection,
  doc,
  getDocs,
  query,
  setDoc,
  Timestamp,
  where,
} from "firebase/firestore";
import { auth, db, getMessagingInstance } from "@/lib/firebase";
import { getToken } from "firebase/messaging";
import { updateDocument } from "@/lib/firestore";

const GOOGLE_PROVIDER = new GoogleAuthProvider();

/**
 * Sign the user in with Google via popup, then ensure a `member_profiles`
 * document exists for the auth UID. Mirrors the iOS Google SignIn flow at
 * `ContentView.swift:545-588` and the Android Credential Manager flow at
 * `SocialSignInButtons.kt:152-214`. Returns the resolved credential so
 * callers can branch on `isNewUser` for routing.
 */
export async function signInWithGoogle(): Promise<{
  credential: UserCredential;
  isNewUser: boolean;
}> {
  const credential = await signInWithPopup(auth, GOOGLE_PROVIDER);
  const user = credential.user;

  // Look up an existing member_profiles doc by authUid.
  const q = query(
    collection(db, "member_profiles"),
    where("authUid", "==", user.uid),
  );
  const snap = await getDocs(q);
  let isNewUser = snap.empty;

  if (isNewUser) {
    const nickname = user.displayName ?? "Member";
    await setDoc(doc(db, "member_profiles", user.uid), {
      authUid: user.uid,
      nickname,
      name: nickname,
      memberType: "adult",
      isActive: true,
      households: [],
      createdAt: Timestamp.now(),
      createdBy: user.uid,
      ...(user.photoURL ? { profile_url: user.photoURL } : {}),
    });
  }

  // Best-effort FCM token registration.
  try {
    const messaging = await getMessagingInstance();
    if (messaging) {
      const token = await getToken(messaging, {
        vapidKey: import.meta.env.NEXT_PUBLIC_FIREBASE_VAPID_KEY,
      });
      if (token) {
        if (isNewUser) {
          await setDoc(
            doc(db, "member_profiles", user.uid),
            { user_devices: [token] },
            { merge: true },
          );
        } else {
          const profileDoc = snap.docs[0];
          const existing =
            (profileDoc.data().user_devices as string[]) ?? [];
          if (!existing.includes(token)) {
            await updateDocument("member_profiles", profileDoc.id, {
              user_devices: [...existing, token],
            });
          }
        }
      }
    }
  } catch {
    // FCM registration is best-effort.
  }

  return { credential, isNewUser };
}
